import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import DashboardLayout from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { 
  Bus, MapPin, Users, Phone, Clock, Navigation, 
  User, AlertCircle, CheckCircle, Loader2
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DriverTransportPage = () => {
  const { getAuthHeader, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('vehicle');
  
  const [vehicleData, setVehicleData] = useState(null);
  const [students, setStudents] = useState([]);
  const [stops, setStops] = useState([]);
  const [routeInfo, setRouteInfo] = useState(null);

  useEffect(() => {
    fetchDriverData();
  }, []);

  const fetchDriverData = async () => {
    setLoading(true);
    try {
      const [vehicleRes, studentsRes, stopsRes] = await Promise.all([
        axios.get(`${API_URL}/api/transport-v2/driver/my-vehicle`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/transport-v2/driver/my-students`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/transport-v2/driver/my-route-stops`, { headers: getAuthHeader() })
      ]);
      
      setVehicleData(vehicleRes.data);
      setStudents(studentsRes.data.students || []);
      setStops(stopsRes.data.stops || []);
      setRouteInfo(stopsRes.data.route);
    } catch (error) {
      console.error('Failed to fetch driver data:', error);
      toast.error('Failed to load transport data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout title="My Transport">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    );
  }

  // No vehicle assigned
  if (!vehicleData?.vehicle) {
    return (
      <DashboardLayout title="My Transport">
        <Card>
          <CardContent className="py-16">
            <div className="text-center">
              <AlertCircle className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-xl font-semibold mb-2">No Vehicle Assigned</h3>
              <p className="text-muted-foreground">
                You don't have any vehicle assigned to you yet. Please contact your administrator.
              </p>
            </div>
          </CardContent>
        </Card>
      </DashboardLayout>
    );
  }

  const vehicle = vehicleData.vehicle;
  const route = vehicleData.route;

  return (
    <DashboardLayout title="My Transport">
      <div className="space-y-6">
        {/* Info Banner */}
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
          <div className="flex items-center gap-2 text-blue-700 dark:text-blue-300">
            <AlertCircle className="h-5 w-5" />
            <span className="font-medium">View Only Mode</span>
          </div>
          <p className="text-sm text-blue-600 dark:text-blue-400 mt-1">
            You can view your assigned vehicle, route, and students. For any changes, please contact the administrator.
          </p>
        </div>

        {/* Vehicle Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="bg-blue-100 dark:bg-blue-900/30 p-3 rounded-lg">
                  <Bus className="h-6 w-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">My Vehicle</p>
                  <p className="text-lg font-bold">{vehicle.vehicle_number}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="bg-green-100 dark:bg-green-900/30 p-3 rounded-lg">
                  <Navigation className="h-6 w-6 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Route</p>
                  <p className="text-lg font-bold">{route?.name || 'Not Assigned'}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="bg-purple-100 dark:bg-purple-900/30 p-3 rounded-lg">
                  <Users className="h-6 w-6 text-purple-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Students</p>
                  <p className="text-lg font-bold">{students.length}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="bg-amber-100 dark:bg-amber-900/30 p-3 rounded-lg">
                  <MapPin className="h-6 w-6 text-amber-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Stops</p>
                  <p className="text-lg font-bold">{stops.length}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Card>
          <CardContent className="p-0">
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <div className="border-b px-4">
                <TabsList className="h-12 bg-transparent">
                  <TabsTrigger value="vehicle" className="gap-2">
                    <Bus className="h-4 w-4" /> Vehicle Details
                  </TabsTrigger>
                  <TabsTrigger value="students" className="gap-2">
                    <Users className="h-4 w-4" /> Students ({students.length})
                  </TabsTrigger>
                  <TabsTrigger value="stops" className="gap-2">
                    <MapPin className="h-4 w-4" /> Route Stops ({stops.length})
                  </TabsTrigger>
                </TabsList>
              </div>

              {/* Vehicle Details Tab */}
              <TabsContent value="vehicle" className="p-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <h3 className="font-semibold text-lg flex items-center gap-2">
                      <Bus className="h-5 w-5" /> Vehicle Information
                    </h3>
                    <div className="space-y-3">
                      <div className="flex justify-between py-2 border-b">
                        <span className="text-muted-foreground">Vehicle Number</span>
                        <span className="font-medium">{vehicle.vehicle_number}</span>
                      </div>
                      <div className="flex justify-between py-2 border-b">
                        <span className="text-muted-foreground">Vehicle Type</span>
                        <Badge variant="outline" className="capitalize">{vehicle.vehicle_type}</Badge>
                      </div>
                      <div className="flex justify-between py-2 border-b">
                        <span className="text-muted-foreground">Capacity</span>
                        <span className="font-medium">{vehicle.capacity} seats</span>
                      </div>
                      <div className="flex justify-between py-2 border-b">
                        <span className="text-muted-foreground">Make/Model</span>
                        <span className="font-medium">{vehicle.make || '-'} {vehicle.model || ''}</span>
                      </div>
                      <div className="flex justify-between py-2 border-b">
                        <span className="text-muted-foreground">Status</span>
                        <Badge className={vehicle.status === 'active' ? 'bg-green-500' : 'bg-amber-500'}>
                          {vehicle.status || 'Active'}
                        </Badge>
                      </div>
                    </div>
                  </div>
                  
                  {route && (
                    <div className="space-y-4">
                      <h3 className="font-semibold text-lg flex items-center gap-2">
                        <Navigation className="h-5 w-5" /> Route Information
                      </h3>
                      <div className="space-y-3">
                        <div className="flex justify-between py-2 border-b">
                          <span className="text-muted-foreground">Route Name</span>
                          <span className="font-medium">{route.name}</span>
                        </div>
                        <div className="flex justify-between py-2 border-b">
                          <span className="text-muted-foreground">Start Point</span>
                          <span className="font-medium">{route.start_point || '-'}</span>
                        </div>
                        <div className="flex justify-between py-2 border-b">
                          <span className="text-muted-foreground">End Point</span>
                          <span className="font-medium">{route.end_point || '-'}</span>
                        </div>
                        <div className="flex justify-between py-2 border-b">
                          <span className="text-muted-foreground">Total Distance</span>
                          <span className="font-medium">{route.total_distance || '-'} km</span>
                        </div>
                        <div className="flex justify-between py-2 border-b">
                          <span className="text-muted-foreground">Estimated Time</span>
                          <span className="font-medium">{route.estimated_time || '-'} mins</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </TabsContent>

              {/* Students Tab */}
              <TabsContent value="students" className="p-6">
                {students.length === 0 ? (
                  <div className="text-center py-12">
                    <Users className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
                    <p className="text-muted-foreground">No students assigned to your route</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Student</TableHead>
                        <TableHead>Class</TableHead>
                        <TableHead>Stop</TableHead>
                        <TableHead>Pickup Time</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>Contact</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {students.map((student) => (
                        <TableRow key={student.id}>
                          <TableCell>
                            <div className="flex items-center gap-3">
                              <div className="h-9 w-9 bg-primary/10 rounded-full flex items-center justify-center">
                                <User className="h-5 w-5 text-primary" />
                              </div>
                              <div>
                                <p className="font-medium">{student.name}</p>
                                <p className="text-xs text-muted-foreground">Roll: {student.roll_number || '-'}</p>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            {student.class_name} {student.section && `- ${student.section}`}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1">
                              <MapPin className="h-4 w-4 text-muted-foreground" />
                              {student.stop?.name || '-'}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1">
                              <Clock className="h-4 w-4 text-muted-foreground" />
                              {student.stop?.pickup_time || '-'}
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className="capitalize">
                              {student.pickup_type || 'both'}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            {student.parent_phone || student.phone ? (
                              <a 
                                href={`tel:${student.parent_phone || student.phone}`}
                                className="flex items-center gap-1 text-blue-600 hover:underline"
                              >
                                <Phone className="h-4 w-4" />
                                {student.parent_phone || student.phone}
                              </a>
                            ) : '-'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </TabsContent>

              {/* Stops Tab */}
              <TabsContent value="stops" className="p-6">
                {stops.length === 0 ? (
                  <div className="text-center py-12">
                    <MapPin className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
                    <p className="text-muted-foreground">No stops on your route</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {stops.map((stop, index) => (
                      <div 
                        key={stop.id}
                        className="flex items-start gap-4 p-4 border rounded-lg"
                      >
                        <div className="flex flex-col items-center">
                          <div className={`h-10 w-10 rounded-full flex items-center justify-center text-white font-bold ${
                            index === 0 ? 'bg-green-500' : index === stops.length - 1 ? 'bg-red-500' : 'bg-blue-500'
                          }`}>
                            {index + 1}
                          </div>
                          {index < stops.length - 1 && (
                            <div className="w-0.5 h-8 bg-gray-300 mt-2" />
                          )}
                        </div>
                        <div className="flex-1">
                          <h4 className="font-semibold">{stop.name}</h4>
                          <div className="flex flex-wrap gap-4 mt-2 text-sm text-muted-foreground">
                            <div className="flex items-center gap-1">
                              <Clock className="h-4 w-4" />
                              Pickup: {stop.pickup_time || '-'}
                            </div>
                            <div className="flex items-center gap-1">
                              <Clock className="h-4 w-4" />
                              Drop: {stop.drop_time || '-'}
                            </div>
                            {stop.distance_from_school && (
                              <div className="flex items-center gap-1">
                                <Navigation className="h-4 w-4" />
                                {stop.distance_from_school} km from school
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default DriverTransportPage;
